<template>
  <div class="tag">
    <div class="loading" v-if="loading">
      Loading...
    </div>

    <div v-if="error" class="error">
      {{ error }}
    </div>

    <div v-if="tag" class="tag-wrapper">
      <div class="content">
        <header class="tag-header" :style="{ 'border-color': tag.color, 'background-color': tag.color + 10 }">
          <h1>{{tag.name}}</h1>
          <a class="edit-button" target="_blank" v-if="tag._id" :href="`http://localhost:3333/desk/tag;${tag._id}`">Edit</a>
        </header>

        <div class="main-content">
          <div v-html="overviewHtml" class="overview" />
          <h2>Scenes ({{ tag.scenes.length }})</h2>
          <ul class="scene-list">
            <li v-for="scene in tag.scenes" class="list__item" :key="scene._id">
              <div class="scene-tile">
                <router-link class="scene-tile__image-link" :to="{name: 'scene', params: {id: scene._id}}">
                  <div class="scene-tile__preview-container">
                    <img class="thumbnail" v-if="scene.thumbnail" :src="imageUrlFor(scene.thumbnail).ignoreImageParams().width(480)"/>
                    <img class="screens" v-if="scene.screencaps" :src="imageUrlFor(scene.screencaps).ignoreImageParams()"/>
                  </div>
                </router-link>
                <router-link class="scene-tile__name" :to="{name: 'scene', params: {id: scene._id}}">
                  <span>{{scene.name}}<div v-if="scene.favorite" class="scene-tile__favorite"></div></span>
                  <div class="scene-tile__date">{{new Date(scene.release).toLocaleDateString('en-us', { weekday:"long", year:"numeric", month:"short", day:"numeric"})}}</div>
                </router-link>

                <h3 class="scene-tile__title">
                  <div class="scene-tile__meta-wrapper">
                    <div class="scene-tile__studio-wrapper">
                      <ul class="scene-tile__studios" v-if="scene.studios" >
                        <li class="scene-tile__studio" v-for="studio in scene.studios" :style="{ 'border-color': studio.color, 'background-color': studio.color + '0d' }">
                          <router-link :to="{name: 'studio', params: {id: studio._id}}" >
                            <div class="scene-tile__studio-name">{{studio.name}}</div>
                          </router-link>
                        </li>
                      </ul>
                    </div>
                    <div class="scene-tile__details">
                      <ul v-if="scene.models" class="scene-tile__models">
                        <li class="scene-tile__model" v-for="model in scene.models">
                          <router-link class="scenes__model-link" :to="{name: 'model', params: {id: model._id}}">
                            <div class="scene-tile__model-image"
                              v-bind:style="[{ backgroundImage: `url(${imageUrlFor(model.imageHeadshot)})`}]">
                            </div>
                            <div class="scene-tile__model-name">
                              {{model.name}}
                            </div>
                          </router-link>
                        </li>
                      </ul>
                    </div>
                  </div>
                </h3>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import sanity from "../sanity";
import imageUrlBuilder from "@sanity/image-url";

const imageBuilder = imageUrlBuilder(sanity);

const query = `*[_type == "tag" && _id == $id] {
  _id,
  name,
  color,
  "scenes": *[_type=='scene' && tags[]._ref match $id]{
    _id,
    name,
    release,
    models,
    thumbnail,
    screencaps,
    models[]->
  }
}[0]
`;

export default {
  props: {
    id: {
      type: String
    }
  },
  name: "Tag",
  data() {
    return {
      loading: true,
      tag: null
    };
  },
  created() {
    this.fetchData();
  },
  watch: {
    $route: "fetchData"
  },
  methods: {
    imageUrlFor(source) {
      return imageBuilder.image(source);
    },
    fetchData() {
      this.error = this.post = null;
      this.loading = true;
      sanity.fetch(query, { id: this.id }).then(
        tag => {
          this.loading = false;
          this.tag = tag;
        },
        error => {
          this.error = error;
        }
      );
    }
  }
};
</script>

<style scoped>
.tag {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0;
  padding: 36px 0;
  grid-gap: 36px;
}
.tag-wrapper {
  width: 100%;
}
.tag__name {
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--color-mono-0);
  text-decoration: none; 
}
.main-content {
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
}
.main-content h2 {
  margin: 36px 36px 0;
  font-size: 1rem;
}
.tag__image-link {
  width: 100%;
}
.scene-list {
  display: grid;
  margin: 0;
  padding: 36px;
  grid-gap: 36px;
  grid-template-columns: 1fr;
}
@media (min-width: 1000px) {
  .scene-list {
    grid-template-columns: 1fr 1fr;
  }
}
.scene-list li {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.scenes-list__model {
  display: block;
  font-size: 1rem;
}

.scenes__title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 16px;
  margin-bottom: 12px;
  border-bottom: solid 1px var(--color-mono-5);
  width: 100%;
}
.scenes__title-wrapper {
  display: flex;
  justify-content: flex-start;
  align-items: center;
}
.scene-tile__tags {
  display: inline-flex;
  justify-content: flex-start;
  align-items: center;
  margin: 0;
  padding: 0;
  font-size: 1.125rem;
}
.scene-tile__tags .scene-tile__tag {
  display: inline-flex;
  justify-content: center;
  font-size: 1rem;
  color: var(--color-mono-0);
  font-weight: 500;
  border-width: 1px;
  border-style: solid;
  padding: 4px 12px;
  margin-right: 12px;
  border-radius: 5px;
}
.scene-tile__tags .scene-tile__tag a {
  font-size: 0.875rem;
  color: var(--color-mono-0);
  font-weight: 500;
  text-decoration: none;
}
.scene-tile__name {
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--color-mono-0);
  text-decoration: none;
  width: 100%;
  height: 100%;
  display: block;
}
.scene-tile__date {
  font-size: 0.625rem;
  font-weight: 600;
}
.scene-tile__image-link {
  width: 100%;
}
.scene-tile__models {
    display: flex;
    width: 100%;
    margin: 0;
    padding: 0;
    justify-content: flex-start;
    align-items: center;
}
.scene-tile__model {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  height: 36px;
  align-items: center;
  display: flex;
  margin-right: 18px;
}
.scene-tile__model:hover .scene-tile__model-image {
  transform: scale(1.075) translateY(-50%);
}
.scenes__model-title {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  color: var(--color-mono-0);
  text-decoration: none;
}
.scene-tile__model-name {
  font-size: 12px;
  padding-left: 44px;
  white-space: nowrap;
  font-weight: 500;
}
.scene-tile__model-image {
  display: block;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: var(--color-mono-5);
  top: 50%;
  left: 0;
  position: absolute;
  transform: translateY(-50%);
  transform-origin: center;
  transition: transform 0.5s ease-in-out;
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
}
.scenes__model-link {
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: var(--color-mono-0);
  text-decoration: none;
  display: flex;
  height: 100%;
}
header.tag-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  border-radius: 5px;
  padding: 24px;
  margin: 0 36px 0;
  min-height: 150px;
  height: 10vw;
  border-width: 1px;
  border-style: solid;
}
</style>
